/**
 * Customer PWA analytics React context — Analytics Core Engine v2.2.5
 * §2.3, §9.2.
 */
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import type { AnalyticsFlow } from 'pi-kiosk-shared';
import { ANALYTICS_IDENTITY_EVENTS } from 'pi-kiosk-shared';
import { buildScreenMetadata } from 'pi-kiosk-shared/analytics/metadataBuilders';
import { useApiClient } from '../providers/AppDataProvider.js';
import { useRuntimeConfig } from '../providers/RuntimeConfigProvider.js';
import { useTenantCode } from '../tenant/TenantProvider.js';
import { resolveCustomerApiTenantCode } from '../tenant/customerApiTenant.js';
import {
  useCustomerAnalyticsManager,
  type CustomerAnalyticsManager,
} from './useCustomerAnalyticsManager.js';
import { registerCustomerAuthLifecycleHooks } from '../session/customerAuthLifecycle.js';
import { useCustomerSession } from '../session/CustomerSessionProvider.js';

const CustomerAnalyticsContext = createContext<CustomerAnalyticsManager | null>(null);

const NOOP_MANAGER: CustomerAnalyticsManager = {
  getSessionId: () => null,
  track: async () => {
    /* no-op */
  },
  completeSession: async () => {
    /* no-op */
  },
  abandonSession: async () => {
    /* no-op */
  },
  bindSessionId: () => {
    /* no-op */
  },
};

interface ProviderProps {
  readonly flow?: AnalyticsFlow | null;
  readonly kioskId?: number | null;
  readonly boundSessionId?: string | null;
  readonly analyticsConsentGranted?: boolean;
  readonly analyticsConsentVersion?: string;
  readonly analyticsConsentDecidedAtMs?: number | null;
  readonly children: ReactNode;
}

export function CustomerAnalyticsProvider({
  flow,
  kioskId,
  boundSessionId,
  analyticsConsentGranted,
  analyticsConsentVersion,
  analyticsConsentDecidedAtMs,
  children,
}: ProviderProps): JSX.Element {
  const api = useApiClient();
  const browseTenantCode = useTenantCode();
  const apiTenantCode = resolveCustomerApiTenantCode(browseTenantCode);
  const config = useRuntimeConfig();
  const manager = useCustomerAnalyticsManager({
    api: apiTenantCode !== null ? api : null,
    tenantCode: apiTenantCode ?? browseTenantCode,
    apiBaseUrl: config.apiBaseUrl,
    kioskId: kioskId ?? null,
    flow: flow ?? null,
    consentIngestGateEnabled: config.consentIngestGateEnabled,
    analyticsConsentGranted,
    analyticsConsentVersion,
    analyticsConsentDecidedAtMs: analyticsConsentDecidedAtMs ?? null,
  });
  const { session, sessionHydrated } = useCustomerSession();
  const identityRecognizedRef = useRef(false);

  useEffect(() => {
    if (
      apiTenantCode === null ||
      !sessionHydrated ||
      session === null ||
      identityRecognizedRef.current
    ) {
      return;
    }
    identityRecognizedRef.current = true;
    manager.track({
      eventName: ANALYTICS_IDENTITY_EVENTS.IDENTITY_RECOGNIZED,
      screenName: 'session_restore',
      metadata: buildScreenMetadata({ screenName: 'session_restore' }),
    });
  }, [apiTenantCode, manager, session, sessionHydrated]);

  useEffect(() => {
    if (typeof boundSessionId === 'string' && boundSessionId.length > 0) {
      manager.bindSessionId(boundSessionId);
    }
  }, [boundSessionId, manager]);

  useEffect(() => {
    return registerCustomerAuthLifecycleHooks({
      onSessionEnded: (reason) => {
        void manager.abandonSession(reason);
      },
    });
  }, [manager]);

  return (
    <CustomerAnalyticsContext.Provider value={manager}>
      {children}
    </CustomerAnalyticsContext.Provider>
  );
}

export function useCustomerAnalytics(): CustomerAnalyticsManager {
  const ctx = useContext(CustomerAnalyticsContext);
  return ctx ?? NOOP_MANAGER;
}

export function useOptionalCustomerAnalytics(): CustomerAnalyticsManager | null {
  return useContext(CustomerAnalyticsContext);
}
